import { apiURL } from "./api_tbeams";

/**
 * Every document upload is proxied by the backend, which stores the file on
 * the shared Lin CDN (and falls back to its local /uploads folder). Files
 * therefore come back either as an absolute CDN URL or a root-relative
 * `/uploads/<name>` path, and legacy rows may also hold a bare filename.
 *
 * `resolveFileUrl` turns any of those into something the browser can open.
 */
export function resolveFileUrl(storedPath) {
  if (!storedPath) return "";
  const value = String(storedPath);
  if (/^(https?:)?\/\//i.test(value)) return value;
  if (/^(blob:|data:)/i.test(value)) return value;
  const name = value.replace(/^\/?uploads\//i, "").replace(/^\/+/, "");
  return `${apiURL}/uploads/${name}`;
}

/**
 * Human-readable filename for a stored file reference, with the generated
 * `<timestamp>-<index>-<random>-` prefix stripped so users see the name they
 * uploaded. Works for CDN URLs and legacy /uploads paths.
 */
export function fileNameFromUrl(storedPath) {
  if (!storedPath) return "";
  const value = String(storedPath).split("?")[0];
  const segments = value.split("/");
  const last = segments[segments.length - 1] || value;
  return decodeURIComponent(last).replace(/^\d+-\d+-[0-9a-f]{8}-/i, "");
}

const defaultUploadId = () =>
  `up_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/**
 * Poll the backend's per-file upload progress while the (slow) CDN leg runs.
 * Best-effort: failures are ignored and the caller still awaits the upload.
 *
 * @returns {Function} stop polling
 */
export function pollUploadProgress(uploadId, onProgress, { intervalMs = 1200 } = {}) {
  if (!uploadId || typeof onProgress !== "function") return () => {};

  let active = true;
  let timer = null;

  const tick = async () => {
    if (!active) return;
    try {
      const response = await fetch(
        `${apiURL}/upload_progress/${encodeURIComponent(uploadId)}`,
      );
      const data = await response.json();
      const files = data && data.progress && data.progress.files;
      if (Array.isArray(files) && files.length > 0) {
        const done = files.filter((f) => f.status === "done").length;
        const total = files.reduce((sum, f) => sum + (f.total || 0), 0);
        const loaded = files.reduce((sum, f) => sum + (f.loaded || 0), 0);
        const percent =
          total > 0
            ? Math.min(99, Math.round((loaded / total) * 100))
            : Math.round((done / files.length) * 100);
        onProgress({ files, percent, done, totalFiles: files.length });
      }
    } catch (err) {
      // Progress is a nicety — never let it break an upload.
      console.debug("Upload progress unavailable:", err);
    }
    if (active) timer = setTimeout(tick, intervalMs);
  };

  timer = setTimeout(tick, intervalMs);

  return () => {
    active = false;
    if (timer) clearTimeout(timer);
  };
}

/**
 * Upload files through a backend upload endpoint (default `/post_images`,
 * which stores documents on the Lin CDN).
 *
 * @param {Object}   options
 * @param {File[]}   options.files       files to upload
 * @param {Object}   [options.form]      JSON metadata expected by the endpoint
 * @param {string}   [options.endpoint]  backend path, e.g. "/memo/upload"
 * @param {string}   [options.fieldName] multipart field name (default "files")
 * @param {Function} [options.onProgress] receives { percent, files, done, totalFiles }
 * @returns {Promise<Object>} parsed backend response
 */
export async function uploadAttachments({
  files,
  form,
  endpoint = "/post_images",
  fieldName = "files",
  onProgress,
  uploadId,
}) {
  const list = Array.from(files || []);
  if (list.length === 0) {
    return { status: "success", filesUploaded: 0, files: [] };
  }

  const id = uploadId || defaultUploadId();
  const formData = new FormData();
  list.forEach((file) => formData.append(fieldName, file));
  // The upload id travels as its own multipart field so the backend can report
  // progress per file while it streams them to the CDN.
  formData.append("upload_id", id);
  formData.append("form", JSON.stringify({ ...(form || {}), upload_id: id }));

  const stopPolling = pollUploadProgress(id, onProgress);

  try {
    const response = await fetch(`${apiURL}${endpoint}`, {
      method: "POST",
      body: formData,
    });

    const raw = await response.text();
    let result;
    try {
      result = JSON.parse(raw);
    } catch (parseErr) {
      result = {
        status: "failed",
        success: false,
        message: raw.slice(0, 300) || "Unexpected server response",
      };
    }

    if (!response.ok || result.status === "failed" || result.success === false) {
      throw new Error(
        result.message || result.error || `Upload failed (HTTP ${response.status})`,
      );
    }

    if (typeof onProgress === "function") {
      onProgress({ percent: 100, done: list.length, totalFiles: list.length });
    }

    return result;
  } finally {
    stopPolling();
  }
}
